import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getSuggestions } from '@/utils/newsUtils';

const navLinkClass =
  'px-3 py-2 text-sm font-medium text-foreground-700 rounded-md transition-colors hover:text-primary-600 hover:bg-primary-50 cursor-pointer whitespace-nowrap';

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const today = new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const suggestions = getSuggestions(keyword);
  const activeCategory = categories.find((c) => location.pathname === `/category/${c.id}`);
  const isHome = location.pathname === '/';

  const submitSearch = (value?: string) => {
    const q = (value ?? keyword).trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden md:block bg-foreground-950 text-background-100">
        <div className="w-full px-4 md:px-8 h-9 flex items-center justify-between text-xs">
          <span className="text-background-300">{today}</span>
          <div className="flex items-center gap-4">
            <span className="text-background-300">今日热搜</span>
            {['古装剧定档', '城市漫步', '社区食堂', '电子榨菜'].map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="text-background-100 hover:text-primary-300 transition-colors cursor-pointer"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
              <span className="w-9 h-9 rounded-lg bg-primary-500 text-primary-50 flex items-center justify-center text-lg font-bold font-heading">
                瓜
              </span>
              <span className="text-xl font-bold font-heading tracking-tight text-foreground-950">
                吃瓜网
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <Link to="/" className={`${navLinkClass} ${isHome ? 'text-primary-600 bg-primary-50' : ''}`}>
                首页
              </Link>

              <div className="relative" ref={catRef}>
                <button
                  type="button"
                  onClick={() => setCatOpen((v) => !v)}
                  className={`${navLinkClass} flex items-center gap-1 ${activeCategory ? 'text-primary-600 bg-primary-50' : ''}`}
                >
                  分类
                  <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {catOpen && (
                  <div className="absolute left-0 top-full mt-2 w-[420px] rounded-lg border border-background-300 bg-background-50 p-2 animate-fade-in">
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/category/${c.id}`}
                        className="flex items-start gap-3 rounded-md p-3 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                          <i className={`${c.icon} text-lg`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-foreground-950">{c.name}</span>
                          <span className="mt-0.5 block text-xs text-foreground-500 line-clamp-2">{c.description}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/about" className={`${navLinkClass} ${location.pathname === '/about' ? 'text-primary-600 bg-primary-50' : ''}`}>
                关于我们
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={`${searchOpen ? 'ri-close-line' : 'ri-search-line'} text-xl`}></i>
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="md:hidden w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="w-full px-4 md:px-8 pb-4 animate-fade-in">
            <div className="max-w-2xl mx-auto relative">
              <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSearch();
                  }}
                  placeholder="搜索热点、明星、话题关键词"
                  className="flex-1 h-11 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                <button
                  type="button"
                  onClick={() => submitSearch()}
                  className="h-8 px-3 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
              {keyword.trim() && (
                <div className="absolute left-0 right-0 top-full mt-2 rounded-lg border border-background-300 bg-background-50 p-2">
                  {suggestions.length > 0 ? (
                    suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => submitSearch(s.title)}
                        className="w-full text-left flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-search-line text-foreground-400"></i>
                        <span className="truncate">{s.title}</span>
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-foreground-400">暂无联想结果（演示提示）</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 animate-fade-in">
          <nav className="px-4 py-3 flex flex-col gap-1">
            <Link to="/" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
              首页
            </Link>
            <button
              type="button"
              onClick={() => setMobileCatOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="pl-2 flex flex-col gap-1">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className={`${c.icon} text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                setSearchOpen(true);
              }}
              className="flex items-center gap-2 px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
            <Link to="/about" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}